import type {
  CardElement,
  ElementsErrorEvent,
  CardElementEventName,
  ElementsResizeEvent,
  PaypercutBillingDetails,
  PaypercutPaymentMethodFutureUsage,
} from '../types/elements';
import { Emitter } from '../utils/emitter';
import {
  registerDeveloperAssistantTarget,
  type DeveloperAssistantRegistration,
} from './developer-assistant-registration';
import { isElementVisible } from './element-visibility';
import {
  ELEMENTS_COMPONENT_BOOTSTRAP_CHANNEL,
  ElementsComponentMessageType,
  type ElementsComponentConnectMessage,
  type ElementsComponentEventPayload,
  type ElementsSubmissionCancellationReason,
  isElementsComponentFrameReadyMessage,
  isElementsPreviewProgressEvent,
} from './controller-protocol';
import type { ElementsGroupContext } from './group';
import { buildCardElementFrameUrl, withElementsFrameNavigationIdentity } from './frame-url';
import { generateId } from '../utils/generate-id';
import { createSubmitError, type PaymentMethodSubmissionSource } from './payment-method-submission';
import {
  stageAppearanceTransitionFrame,
  type AppearanceTransitionFrame,
  type PreparedAppearanceTransition,
} from './appearance-transition-frame';

const MAX_IFRAME_HEIGHT = 8192;
const DEVELOPER_PRESET_TIMEOUT_MS = 5_000;
const CARD_ELEMENT_VALIDATION_TIMEOUT_MS = 15_000;
// Combined Card waits for three provider-owned secure fields plus final geometry.
// Match the split-field provider allowance so a slow but healthy sandbox does
// not lose the bounded initial reload race before reaching Interactive Ready.
export const CARD_ELEMENT_INITIALIZATION_TIMEOUT_MS = 40_000;
const CARD_ELEMENT_INITIALIZATION_RELOAD_LIMIT = 1;

interface PendingDeveloperPreset {
  requestId: string;
  presetId: string;
  resolve: () => void;
  reject: (error: Error) => void;
  timeoutId: number;
}

interface PendingValidation {
  requestId: string;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: Error) => void;
  timeoutId: number;
}

interface PendingAppearanceRemount {
  resolve(transition: PreparedAppearanceTransition): void;
  reject(error: Error): void;
  transition: AppearanceTransitionFrame;
  wasReady: boolean;
  settled: boolean;
  automaticRecoveryAttempted: boolean;
}

export class CardElementImpl implements CardElement, PaymentMethodSubmissionSource {
  readonly componentId = generateId();
  readonly componentType = 'card' as const;
  componentGeneration = 0;
  readonly submissionSourceId = this.componentId;
  private readonly emitter = new Emitter<CardElementEventName>();
  private iframe: HTMLIFrameElement | null = null;
  private mounted = false;
  private connected = false;
  private destroyed = false;
  private ready = false;
  private developerAssistant: DeveloperAssistantRegistration | null = null;
  private pendingDeveloperPreset: PendingDeveloperPreset | null = null;
  private pendingValidation: PendingValidation | null = null;
  private initializationTimeoutId: number | null = null;
  private initializationReloads = 0;
  private pendingAppearanceRemount: PendingAppearanceRemount | null = null;
  private appearancePreviewError: HTMLElement | null = null;
  private readonly messageHandler = (event: MessageEvent): void => this.onWindowMessage(event);

  constructor(private readonly group: ElementsGroupContext) {}

  mount(target: string | HTMLElement): void {
    if (this.destroyed) {
      throw new Error('[Paypercut Elements] Cannot mount a destroyed Card Element.');
    }
    if (this.mounted) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('[Paypercut Elements] Card Element can only be mounted in a browser.');
    }
    if (this.group.isDestroyed()) {
      throw new Error('[Paypercut Elements] Cannot mount an Element from a destroyed group.');
    }

    const container = resolveContainer(target);
    const iframe = this.createFrame();

    this.iframe = iframe;
    this.mounted = true;
    this.group.markComponentMounted();
    window.addEventListener('message', this.messageHandler);
    this.group.ensureController();
    container.appendChild(iframe);
    this.armInitializationWatch(iframe);
  }

  on(event: 'ready', handler: () => void): () => void;
  on(event: 'resize', handler: (event: ElementsResizeEvent) => void): () => void;
  on(event: 'error', handler: (event: ElementsErrorEvent) => void): () => void;
  on(
    event: CardElementEventName,
    handler: ((event: ElementsResizeEvent | ElementsErrorEvent) => void) | (() => void),
  ): () => void {
    return this.emitter.on(event, handler);
  }

  assertCanSubmitPaymentMethod(): void {
    if (this.destroyed || this.group.isDestroyed()) {
      throw createSubmitError('elements_destroyed', 'The Card Element has been destroyed.', false);
    }
    if (!this.mounted) {
      throw createSubmitError(
        'elements_not_mounted',
        'The Card Element has not been mounted.',
        true,
      );
    }
    if (!this.ready) {
      throw createSubmitError('elements_not_ready', 'The Card Element is not ready yet.', true);
    }
    if (this.pendingDeveloperPreset) {
      throw createSubmitError(
        'elements_test_preset_in_progress',
        'Wait for the test card preset to finish filling before submitting.',
        true,
      );
    }
  }

  validatePaymentMethod(): Promise<void> {
    try {
      this.assertCanSubmitPaymentMethod();
    } catch (error) {
      return Promise.reject(error);
    }
    if (this.pendingValidation) {
      return this.pendingValidation.promise;
    }

    const requestId = generateId();
    let resolveValidation!: () => void;
    let rejectValidation!: (error: Error) => void;
    const promise = new Promise<void>((resolve, reject) => {
      resolveValidation = resolve;
      rejectValidation = reject;
    });
    const pending: PendingValidation = {
      requestId,
      promise,
      resolve: resolveValidation,
      reject: rejectValidation,
      timeoutId: window.setTimeout(() => {
        if (this.pendingValidation !== pending) return;
        this.pendingValidation = null;
        rejectValidation(
          createSubmitError(
            'elements_submit_timeout',
            'Validating the payment details timed out. Please try again.',
            true,
          ),
        );
      }, CARD_ELEMENT_VALIDATION_TIMEOUT_MS),
    };
    this.pendingValidation = pending;
    try {
      this.group.validateComponent(this.componentId, requestId);
    } catch {
      this.settleValidation(
        createSubmitError(
          'elements_submit_failed',
          'The secure card form could not be validated.',
          true,
        ),
      );
    }
    return promise;
  }

  beginPaymentMethodSubmission(
    requestId: string,
    deadlineAt: number,
    _futureUsage?: PaypercutPaymentMethodFutureUsage,
    billingDetails?: PaypercutBillingDetails,
  ): void {
    this.clearInitializationWatch();
    this.developerAssistant?.collapse();
    this.developerAssistant?.refresh();
    this.group.submitComponent(this.componentId, requestId, deadlineAt, billingDetails);
  }

  cancelPaymentMethodSubmission(
    requestId: string,
    reason: ElementsSubmissionCancellationReason,
  ): void {
    try {
      this.group.cancelComponentSubmission(this.componentId, requestId, reason);
    } catch {
      // The coordinator owns local settlement if the controller is unavailable.
    }
  }

  attachPaymentMethodActionChannel(requestId: string, actionId: string, port: MessagePort): void {
    this.group.attachActionChannel(this.componentId, requestId, actionId, port);
  }

  onPaymentMethodSubmissionStateChange(): void {
    this.developerAssistant?.collapse();
    this.developerAssistant?.refresh();
  }

  hasActiveSubmission(): boolean {
    return (
      this.group.hasPaymentMethodSubmissionState(this.componentId) ||
      this.pendingValidation !== null ||
      this.pendingDeveloperPreset !== null
    );
  }

  getComponentOptions(): Record<string, never> {
    return {};
  }

  handleControllerError(error: ElementsErrorEvent): void {
    if (this.destroyed) return;
    this.clearInitializationWatch();
    this.ready = false;
    this.settleValidation(createSubmitError(error.code, error.message, error.recoverable ?? true));
    this.emitter.emit('error', error);
  }

  handleControllerEvent(payload: ElementsComponentEventPayload): void {
    if (this.destroyed) return;
    const appearanceRemount = this.pendingAppearanceRemount;
    if (appearanceRemount && !isElementsPreviewProgressEvent(payload)) {
      if (payload.event === 'error') {
        this.rejectAppearanceRemount(
          new Error('The replacement Card Element could not render its Appearance.'),
        );
      }
      return;
    }
    switch (payload.event) {
      case 'ready':
        if (!this.ready) {
          this.ready = true;
          this.clearInitializationWatch();
          if (appearanceRemount && appearanceRemount === this.pendingAppearanceRemount) {
            appearanceRemount.resolve(
              this.prepareSettledAppearanceTransition(appearanceRemount),
            );
            break;
          }
          this.emitter.emit('ready');
          this.group.discoverDeveloperPresets(this.componentId);
        }
        break;
      case 'secure_field_surface_invalid':
        if (!appearanceRemount || appearanceRemount !== this.pendingAppearanceRemount) break;
        if (!appearanceRemount.automaticRecoveryAttempted) {
          this.retryInvalidAppearanceGeometry(appearanceRemount);
          break;
        }
        this.showAppearancePreviewFailure();
        this.rejectAppearanceRemount(
          new Error('The replacement Card Element reported invalid secure-field geometry twice.'),
        );
        break;
      case 'resize':
        this.resize(payload.height);
        break;
      case 'error':
        this.handleControllerError(payload);
        break;
      case 'phase':
        this.developerAssistant?.refresh();
        break;
      case 'action_required':
      case 'submit_result':
        break;
      case 'validation_result':
        if (payload.requestId !== this.pendingValidation?.requestId) break;
        if (payload.result.status === 'succeeded') this.settleValidation();
        else {
          this.settleValidation(
            createSubmitError(
              payload.result.code,
              payload.result.message,
              payload.result.recoverable,
            ),
          );
        }
        break;
      case 'developer_catalog':
        this.registerDeveloperPresets(payload.presets);
        break;
      case 'developer_apply_result':
        this.handleDeveloperApplyResult(payload);
        break;
      case 'change':
        this.group.invalidateSubmissionValidation();
        break;
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.rejectAppearanceRemount(
      new Error('The Card Element was destroyed during Appearance Preview.'),
    );
    this.ready = false;
    this.clearInitializationWatch();
    this.settleValidation(
      createSubmitError('elements_destroyed', 'The Card Element has been destroyed.', false),
    );
    this.clearDeveloperAssistant('The card form was destroyed.');
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.messageHandler);
    }
    this.iframe?.remove();
    this.appearancePreviewError?.remove();
    this.appearancePreviewError = null;
    this.iframe = null;
    this.mounted = false;
    this.emitter.clear();
    this.group.remove(this);
  }

  remountAppearance(): Promise<PreparedAppearanceTransition> {
    this.rejectAppearanceRemount(new Error('A newer Appearance Preview superseded this one.'));
    const frame = this.iframe;
    const parent = frame?.parentNode;
    if (this.destroyed || !this.mounted || !frame || !parent) {
      return Promise.resolve({ commit: () => undefined, rollback: () => undefined });
    }
    let resolveRemount!: (transition: PreparedAppearanceTransition) => void;
    let rejectRemount!: (error: Error) => void;
    const promise = new Promise<PreparedAppearanceTransition>((resolve, reject) => {
      resolveRemount = resolve;
      rejectRemount = reject;
    });
    const wasReady = this.ready;
    const previousGeneration = this.componentGeneration;
    this.ready = false;
    this.componentGeneration += 1;
    this.initializationReloads = 0;
    this.clearInitializationWatch();
    this.group.invalidateSubmissionValidation();
    this.settleValidation(
      createSubmitError(
        'elements_not_ready',
        'The Card Element is refreshing its Appearance.',
        true,
      ),
    );
    try {
      const replacement = this.createFrame();
      const transition = stageAppearanceTransitionFrame(frame, replacement);
      this.pendingAppearanceRemount = {
        resolve: resolveRemount,
        reject: rejectRemount,
        transition,
        wasReady,
        settled: false,
        automaticRecoveryAttempted: false,
      };
      this.iframe = replacement;
      this.group.detachComponent(this);
      this.armInitializationWatch(replacement);
    } catch (error) {
      this.iframe = frame;
      this.ready = wasReady;
      this.componentGeneration = previousGeneration;
      const replacementError =
        error instanceof Error ? error : new Error('Unable to replace the Card Element frame.');
      if (this.pendingAppearanceRemount) this.rejectAppearanceRemount(replacementError);
      else rejectRemount(replacementError);
      return promise;
    }
    return promise;
  }

  cancelAppearanceRemount(componentGeneration: number, error: Error): void {
    if (this.componentGeneration !== componentGeneration) return;
    this.rejectAppearanceRemount(error);
  }

  markAppearanceRecoveryFailed(): void {
    this.ready = false;
    if (this.iframe) {
      this.iframe.inert = true;
      this.iframe.style.pointerEvents = 'none';
    }
    this.emitter.emit('error', {
      code: 'elements_appearance_preview_recovery_failed',
      message: 'The Card Element could not restore its last Appearance.',
      recoverable: true,
    });
  }

  clearAppearancePreviewFailure(): void {
    this.appearancePreviewError?.remove();
    this.appearancePreviewError = null;
  }

  isMounted(): boolean {
    return this.mounted && !this.destroyed;
  }

  private onWindowMessage(event: MessageEvent): void {
    if (
      this.destroyed ||
      !this.iframe?.contentWindow ||
      event.source !== this.iframe.contentWindow ||
      event.origin !== this.group.hostedOrigin ||
      !isElementsComponentFrameReadyMessage(event.data)
    ) {
      return;
    }

    const reinitialized = this.connected;
    this.connected = true;
    if (reinitialized) {
      this.ready = false;
      this.group.invalidateSubmissionValidation();
      this.settleValidation(
        createSubmitError(
          'elements_reinitialized',
          'The secure card form reloaded while it was being validated.',
          true,
        ),
      );
      if (!this.hasActiveSubmission()) this.armInitializationWatch(this.iframe);
    }
    const channel = new MessageChannel();
    const message: ElementsComponentConnectMessage = {
      channel: ELEMENTS_COMPONENT_BOOTSTRAP_CHANNEL,
      type: ElementsComponentMessageType.Connect,
      controllerId: this.group.controllerId,
      componentId: this.componentId,
      componentType: this.componentType,
    };
    try {
      this.iframe.contentWindow.postMessage(message, this.group.hostedOrigin, [channel.port2]);
      if (reinitialized) this.group.reattachComponent(this, channel.port1);
      else this.group.attachComponent(this, channel.port1);
    } catch {
      channel.port1.close();
      channel.port2.close();
      if (this.pendingAppearanceRemount) {
        this.rejectAppearanceRemount(
          new Error('The replacement Card Element could not connect to its controller.'),
        );
        return;
      }
      this.handleControllerError({
        code: 'elements_component_connect_failed',
        message: 'The secure card form could not be connected.',
        recoverable: true,
      });
    }
  }

  private resize(height: number): void {
    if (!this.iframe || !Number.isFinite(height) || height <= 0 || height > MAX_IFRAME_HEIGHT) {
      return;
    }
    const roundedHeight = Math.ceil(height);
    this.iframe.style.height = `${roundedHeight}px`;
    this.emitter.emit('resize', { height: roundedHeight });
  }

  private armInitializationWatch(frame: HTMLIFrameElement): void {
    this.clearInitializationWatch();
    this.initializationTimeoutId = window.setTimeout(() => {
      this.initializationTimeoutId = null;
      if (
        this.destroyed ||
        !this.mounted ||
        this.ready ||
        this.iframe !== frame ||
        this.hasActiveSubmission()
      ) {
        return;
      }

      if (this.pendingAppearanceRemount) {
        this.handleInitializationTimeout();
        return;
      }

      if (this.initializationReloads < CARD_ELEMENT_INITIALIZATION_RELOAD_LIMIT) {
        this.initializationReloads += 1;
        const parent = frame.parentNode;
        if (!parent) {
          this.handleInitializationTimeout();
          return;
        }

        const replacement = this.createFrame();
        const previousGeneration = this.componentGeneration;
        this.componentGeneration += 1;
        try {
          this.iframe = replacement;
          parent.replaceChild(replacement, frame);
          this.armInitializationWatch(replacement);
        } catch {
          this.iframe = frame;
          this.componentGeneration = previousGeneration;
          this.handleInitializationTimeout();
        }
        return;
      }

      this.handleInitializationTimeout();
    }, CARD_ELEMENT_INITIALIZATION_TIMEOUT_MS);
  }

  private createFrame(): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    iframe.id = `paypercut-card-element-${this.componentId}`;
    iframe.title = 'Secure card form';
    iframe.referrerPolicy = 'origin';
    iframe.src = withElementsFrameNavigationIdentity(
      buildCardElementFrameUrl({
        hostedOrigin: this.group.hostedOrigin,
        controllerId: this.group.controllerId,
        componentId: this.componentId,
        clientOptions: this.group.getClientOptions(),
        elementsOptions: this.group.getRenderableElementsOptions(),
        referrer: window.location.origin,
      }),
      generateId(),
    );
    // WebKit can restore a stale component browsing context across history traversal.
    // A fresh, non-secret navigation identity prevents that context from matching a
    // newly bootstrapped component. Sensitive configuration remains in the fragment.
    iframe.allow = 'payment *; publickey-credentials-get *; autofill *';
    iframe.setAttribute('allowtransparency', 'true');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('role', 'presentation');
    iframe.setAttribute('scrolling', 'no');
    Object.assign(iframe.style, {
      width: '100%',
      maxWidth: '100%',
      minWidth: '0',
      height: '0',
      margin: '0',
      padding: '0',
      border: '0',
      display: 'block',
      overflow: 'hidden',
      colorScheme: 'light dark',
      transition: 'height 350ms ease-in-out, opacity 400ms ease-in-out 100ms',
      willChange: 'height, opacity',
    });
    return iframe;
  }

  private clearInitializationWatch(): void {
    if (this.initializationTimeoutId !== null && typeof window !== 'undefined') {
      window.clearTimeout(this.initializationTimeoutId);
    }
    this.initializationTimeoutId = null;
  }

  private handleInitializationTimeout(): void {
    if (this.pendingAppearanceRemount) {
      this.rejectAppearanceRemount(
        new Error('The replacement Card Element did not become ready in time.'),
      );
      return;
    }
    this.handleControllerError({
      code: 'elements_component_initialization_timeout',
      message: 'The secure card form did not become ready in time.',
      recoverable: true,
    });
  }

  private retryInvalidAppearanceGeometry(pending: PendingAppearanceRemount): void {
    if (pending !== this.pendingAppearanceRemount || pending.settled) return;
    const previous = pending.transition.previous;
    this.clearInitializationWatch();
    pending.transition.rollback();
    this.iframe = previous;
    this.componentGeneration += 1;
    this.initializationReloads = 0;
    pending.automaticRecoveryAttempted = true;

    try {
      const replacement = this.createFrame();
      pending.transition = stageAppearanceTransitionFrame(previous, replacement);
      this.iframe = replacement;
      this.group.detachComponent(this);
      this.armInitializationWatch(replacement);
    } catch (error) {
      this.rejectAppearanceRemount(
        error instanceof Error
          ? error
          : new Error('Unable to retry the Card Element Appearance Preview.'),
      );
    }
  }

  private showAppearancePreviewFailure(): void {
    if (this.appearancePreviewError?.isConnected) return;
    const parent = this.pendingAppearanceRemount?.transition.previous.parentNode;
    if (!(parent instanceof HTMLElement)) return;
    const notice = document.createElement('div');
    notice.dataset.paypercutAppearancePreviewError = 'true';
    notice.setAttribute('role', 'alert');
    notice.setAttribute('aria-live', 'assertive');
    notice.textContent =
      'Appearance Preview could not produce usable secure fields. Retry the Appearance update or restore a known-good Appearance.';
    Object.assign(notice.style, {
      marginTop: '8px',
      padding: '10px 12px',
      border: '1px solid #f04438',
      borderRadius: '8px',
      color: '#b42318',
      background: '#fff1f0',
      font: '13px/1.4 system-ui, sans-serif',
    });
    parent.appendChild(notice);
    this.appearancePreviewError = notice;
  }

  private rejectAppearanceRemount(error: Error): void {
    const pending = this.pendingAppearanceRemount;
    if (!pending) return;
    this.pendingAppearanceRemount = null;
    if (pending.settled) return;
    pending.settled = true;
    pending.transition.rollback();
    this.iframe = pending.transition.previous;
    this.ready = pending.wasReady;
    pending.reject(error);
  }

  private prepareSettledAppearanceTransition(
    pending: PendingAppearanceRemount,
  ): PreparedAppearanceTransition {
    return {
      commit: () => {
        if (pending.settled) return;
        pending.settled = true;
        if (this.pendingAppearanceRemount === pending) {
          this.pendingAppearanceRemount = null;
        }
        pending.transition.commit();
        queueMicrotask(() => {
          if (this.destroyed) return;
          this.emitter.emit('ready');
          this.group.discoverDeveloperPresets(this.componentId);
        });
      },
      rollback: () => {
        if (pending.settled) return;
        pending.settled = true;
        if (this.pendingAppearanceRemount === pending) {
          this.pendingAppearanceRemount = null;
        }
        pending.transition.rollback();
        this.iframe = pending.transition.previous;
        this.ready = pending.wasReady;
      },
    };
  }

  private settleValidation(error?: Error): void {
    const pending = this.pendingValidation;
    if (!pending) return;
    this.pendingValidation = null;
    window.clearTimeout(pending.timeoutId);
    if (error) pending.reject(error);
    else pending.resolve();
  }

  private registerDeveloperPresets(
    presets: Extract<ElementsComponentEventPayload, { event: 'developer_catalog' }>['presets'],
  ): void {
    if (this.group.getClientOptions().developerAssistant !== true) {
      return;
    }
    if (this.developerAssistant) {
      // Hosted Card republishes the same authorized catalog after an Appearance
      // replacement. Keep the existing target and controls alive so the pending
      // request can release its lock and restore focus on the original control.
      this.developerAssistant.refresh();
      return;
    }
    this.developerAssistant = registerDeveloperAssistantTarget({
      id: `${this.group.controllerId}:${this.componentId}`,
      configurationIdentity: this.group.configurationIdentity,
      appearanceScopeId: this.group.controllerId,
      presets,
      isVisible: () => isElementVisible(this.iframe?.parentElement ?? null),
      isAvailable: () => this.canApplyDeveloperPreset(),
      isAppearanceAvailable: () => this.isMounted(),
      appearancePreviewClearsTestData: () => true,
      canPreview: () => this.group.isAppearancePreviewAvailable(),
      applyPreset: (presetId) => this.applyDeveloperPreset(presetId),
      getAppearance: () => this.group.getAppearance(),
      updateAppearance: (appearance) => this.group.updateAppearance(appearance),
      replaceAppearance: (appearance) => this.group.replaceAppearance(appearance),
      prepareAppearance: (appearance) => this.group.prepareAppearance(appearance),
      prepareAppearanceReplacement: (appearance) =>
        this.group.prepareAppearanceReplacement(appearance),
    });
  }

  private canApplyDeveloperPreset(): boolean {
    return (
      this.group.getClientOptions().developerAssistant === true &&
      this.ready &&
      !this.destroyed &&
      !this.group.hasPaymentMethodSubmissionState(this.componentId) &&
      !this.group.hasCompletedPaymentMethodSubmission(this.componentId) &&
      !this.pendingDeveloperPreset
    );
  }

  private applyDeveloperPreset(presetId: string): Promise<void> {
    if (!this.canApplyDeveloperPreset()) {
      return Promise.reject(new Error('Test card presets are unavailable right now.'));
    }

    const requestId = generateId();
    return new Promise<void>((resolve, reject) => {
      const pending: PendingDeveloperPreset = {
        requestId,
        presetId,
        resolve,
        reject,
        timeoutId: window.setTimeout(() => {
          if (this.pendingDeveloperPreset !== pending) return;
          this.pendingDeveloperPreset = null;
          reject(new Error('The secure card frame did not acknowledge the test preset.'));
          this.developerAssistant?.refresh();
        }, DEVELOPER_PRESET_TIMEOUT_MS),
      };
      this.pendingDeveloperPreset = pending;
      this.developerAssistant?.refresh();
      try {
        this.group.applyDeveloperPreset(this.componentId, requestId, presetId);
      } catch {
        this.pendingDeveloperPreset = null;
        window.clearTimeout(pending.timeoutId);
        reject(new Error('Unable to contact the secure card frame.'));
        this.developerAssistant?.refresh();
      }
    });
  }

  private handleDeveloperApplyResult(
    payload: Extract<ElementsComponentEventPayload, { event: 'developer_apply_result' }>,
  ): void {
    const pending = this.pendingDeveloperPreset;
    if (
      !pending ||
      payload.requestId !== pending.requestId ||
      payload.presetId !== pending.presetId
    ) {
      return;
    }
    this.pendingDeveloperPreset = null;
    window.clearTimeout(pending.timeoutId);
    if (payload.ok) pending.resolve();
    else pending.reject(new Error('The secure card frame rejected the test preset.'));
    this.developerAssistant?.refresh();
  }

  private clearDeveloperAssistant(reason: string): void {
    this.developerAssistant?.unregister();
    this.developerAssistant = null;
    const pending = this.pendingDeveloperPreset;
    if (!pending) return;
    this.pendingDeveloperPreset = null;
    window.clearTimeout(pending.timeoutId);
    pending.reject(new Error(reason));
  }
}

function resolveContainer(target: string | HTMLElement): HTMLElement {
  const container = typeof target === 'string' ? document.querySelector(target) : target;
  if (!(container instanceof HTMLElement)) {
    throw new Error(`[Paypercut Elements] Container not found: ${String(target)}`);
  }
  return container;
}
